import React from 'react'
import {
  Box, Typography, Paper, Stack, Divider, IconButton, Tooltip,
  CircularProgress, Chip, Pagination, FormControl, Select, MenuItem
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

/**
 * Tabla CRUD reutilizable para pantallas de ABM simples.
 * Props:
 *   - columns: [{ field, header }]
 *   - rows: []
 *   - loading: bool
 *   - onEdit: (row) => void
 *   - onDelete: (row) => void
 *   - onRowClick: (row) => void  (opcional)
 *   - showActions: bool (default true)
 */
export default function CrudTable({
  columns = [],
  rows = [],
  loading = false,
  onEdit,
  onDelete,
  onRowClick,
  showActions = true,
  rowsPerPageOptions = [5, 10, 25],
}) {
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[0])

  const displayed = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage))

  React.useEffect(() => {
    if (page > pageCount - 1) setPage(0)
  }, [page, pageCount])

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>Registros</Typography>
          <Typography variant="body2" color="text.secondary">Vista compacta y más moderna para administrar los elementos.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Filas por página</Typography>
          <FormControl size="small" sx={{ minWidth: 96 }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}>
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : displayed.length === 0 ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}>
          <Typography variant="body2" color="text.secondary">Sin resultados</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {displayed.map((row, idx) => {
            const mainValue = columns[0] ? (row[columns[0].field] ?? '') : ''

            return (
              <Paper
                key={row.id || idx}
                variant="outlined"
                onClick={() => onRowClick?.(row)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 12px 28px rgba(31, 122, 224, 0.10)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" color="primary" label={columns[0]?.header || 'Registro'} />
                      {showActions && <Chip size="small" variant="outlined" label="Editable" />}
                    </Stack>
                    <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.25, mb: 0.75 }}>
                      {mainValue || 'Sin dato principal'}
                    </Typography>
                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                      {columns.slice(1).map((col) => (
                        <Box key={col.field} sx={{ minWidth: 120 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: '0.06em' }}>
                            {col.header}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row[col.field] ?? ''}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {showActions && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onEdit?.(row) }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete?.(row) }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      )}

      {!loading && rows.length > 0 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2.5 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}
    </Paper>
  )
}
